import { scopeProjectRef } from "@cmmd-forge/client-runtime";
import type { EnvironmentId, ProjectId } from "@cmmd-forge/contracts";
import { openProjectLeadCompanion } from "~/uiStateStore";
import type { ForgeAutomationRunTarget } from "../NoActiveThreadState.automations";
import { ForgeAutomationRunRoute } from "../automations/ForgeAutomationRunRoute";
import { ProjectAutomationsPanel } from "../project/ProjectAutomationsPanel";
import { ProjectTaskSurface } from "../tasks/ProjectTaskSurface";
import type { ProjectTaskChrome } from "../tasks/useProjectTaskChrome";
import type { CmmdProjectHomeViewProps } from "./CmmdProjectHomeTypes";
import { CmmdProjectOverviewTab } from "./CmmdProjectOverviewTab";
import { CmmdProjectSettingsTab } from "./CmmdProjectSettingsTab";
import { repositoryBindingStateFor } from "./projectExecutionStatus";
import { CmmdProjectTeamTab } from "./CmmdProjectTeamTab";
import { CmmdProjectUpdatesTab } from "./CmmdProjectUpdatesTab";

export type CmmdProjectActiveTabProps = CmmdProjectHomeViewProps & {
  readonly taskChrome: ProjectTaskChrome;
};

export function CmmdProjectActiveTab(props: CmmdProjectActiveTabProps) {
  if (props.activeTab === "overview") {
    return <CmmdProjectOverviewPanel {...props} />;
  }
  if (props.activeTab === "updates") {
    return <CmmdProjectUpdatesPanel {...props} />;
  }
  if (props.activeTab === "tasks") {
    return <CmmdProjectTasksPanel {...props} />;
  }
  if (props.activeTab === "settings") {
    return <CmmdProjectSettingsPanel {...props} />;
  }
  if (props.activeTab === "team") {
    return (
      <CmmdProjectTeamTab
        projectId={String(props.home.project?.id ?? "")}
        members={props.home.members}
        workspaceMembers={props.home.workspaceMembers}
        workspaceMembersLoading={props.home.workspaceMembersLoading}
        workspaceMembersError={props.home.workspaceMembersError}
        agentMembers={props.home.agentMembers ?? []}
        agentMembersLoading={props.home.agentMembersLoading}
        agentMembersError={props.home.agentMembersError ?? null}
        agentEvents={props.home.agentEvents ?? []}
        agentEventsLoading={props.home.agentEventsLoading}
        agentEventsError={props.home.agentEventsError ?? null}
        tasks={props.taskBoard.tasks}
        onCreateAgentMember={props.home.createAgentMember}
        onAddProjectMember={props.home.addProjectMember}
        onRenameAgentMember={props.home.renameAgentMember}
        onRetireAgentMember={props.home.retireAgentMember}
        onUpdateAgentMemberAvatar={props.home.updateAgentMemberAvatar}
        onListAgentMemberInstances={props.home.listAgentMemberInstances}
        onPreviewAgentMemberConsolidation={props.home.previewAgentMemberConsolidation}
        onApplyAgentMemberConsolidation={props.home.applyAgentMemberConsolidation}
      />
    );
  }
  return <CmmdProjectAutomationsTab {...props} />;
}

function CmmdProjectSettingsPanel(props: CmmdProjectHomeViewProps) {
  if (!props.home.project) {
    return null;
  }
  return (
    <CmmdProjectSettingsTab
      project={props.home.project}
      members={props.home.members}
      tasks={props.taskBoard.tasks}
      threadSummaries={props.threadSummaries}
      tasksLoading={props.taskBoard.loading}
      workSessions={props.workSessions}
      bindingState={repositoryBindingStateFor(props.home.executionStatus)}
      executionStatus={props.home.executionStatus}
      forgeRemovalTargets={props.forgeRemovalTargets}
      onUpdateSettings={props.home.updateSettings}
      onUpdateRepository={props.home.updateRepository}
      onOpenWorkSessionThread={props.onOpenWorkSessionThread}
      onManageMembers={() => props.onTabChange("team")}
      onForgeProjectDeleted={async () => props.home.reload()}
      onRefresh={async () => props.home.reload()}
    />
  );
}

function CmmdProjectOverviewPanel(props: CmmdProjectHomeViewProps) {
  if (!props.home.project) {
    return null;
  }
  return (
    <CmmdProjectOverviewTab
      project={props.home.project}
      updates={props.home.updates}
      onUpdateDescription={props.home.updateDescription}
      onAddUpdate={props.home.addUpdate}
      onUpdateUpdate={props.home.updateUpdate}
      onDeleteUpdate={props.home.deleteUpdate}
      updatesTotal={props.home.updatesTotal}
      updatesLoading={props.home.updatesLoading}
      updatesError={props.home.updatesError}
      onViewAllUpdates={() => props.onTabChange("updates")}
    />
  );
}

function CmmdProjectUpdatesPanel(props: CmmdProjectHomeViewProps) {
  if (!props.home.project) {
    return null;
  }
  return (
    <CmmdProjectUpdatesTab
      projectId={props.home.project.id}
      updates={props.home.updates}
      total={props.home.updatesTotal}
      hasMore={props.home.updatesHasMore}
      loading={props.home.updatesLoading}
      error={props.home.updatesError}
      onAdd={props.home.addUpdate}
      onUpdate={props.home.updateUpdate}
      onDelete={props.home.deleteUpdate}
      onLoadMore={props.home.loadMoreUpdates}
    />
  );
}

/** The Tasks tab: the planning board plus any handoff-link invalid-task notice. */
function CmmdProjectTasksPanel(props: CmmdProjectActiveTabProps) {
  const project = props.home.project;
  if (!project) return null;
  const projectId = Number(project.id);
  if (!Number.isInteger(projectId) || projectId <= 0) return null;
  const startingTaskId = resolveStartingTaskId(props.taskWorkSessionLauncher);
  const conversationTaskIds = new Set(
    props.workSessions.allSessions
      .filter((session) => session.threads.some((thread) => thread.kind === "root"))
      .map((session) => session.taskId),
  );
  return (
    <ProjectTaskSurface
      projectId={projectId}
      projectRef={
        props.home.forgeTarget
          ? scopeProjectRef(
              props.home.forgeTarget.environmentId as EnvironmentId,
              props.home.forgeTarget.projectId as ProjectId,
            )
          : null
      }
      routeViewState={props.taskViewRoute}
      onViewStateChange={props.onTaskViewRouteChange}
      board={props.taskBoard}
      selection={props.taskSelection}
      onSelectionChange={props.onTaskSelectionChange}
      invalidTaskNotice={props.invalidTaskNotice}
      projectTitle={project.title}
      projectIconUrl={project.iconUrl ?? null}
      agentMembers={props.home.agentMembers}
      agentMembersLoading={props.home.agentMembersLoading}
      agentMembersError={props.home.agentMembersError}
      projectIcon={project.icon ?? null}
      workItemPrefix={project.workItemPrefix ?? null}
      members={props.home.members}
      threadSummaries={props.threadSummaries}
      startingTaskId={startingTaskId}
      conversationTaskIds={conversationTaskIds}
      onOpenThread={props.onOpenTaskThread}
      {...(props.onOpenTaskThreadSummary
        ? { onOpenThreadSummary: props.onOpenTaskThreadSummary }
        : {})}
      onStartWork={props.onStartTaskWork}
      onProjectLeadThreadReady={openProjectLeadCompanion}
      chrome={props.taskChrome}
      toolbarPlacement="external"
    />
  );
}

function resolveStartingTaskId(
  launcher: CmmdProjectHomeViewProps["taskWorkSessionLauncher"],
): number | null {
  return launcher.starting ? (launcher.task?.id ?? null) : null;
}

function CmmdProjectAutomationsTab(props: CmmdProjectHomeViewProps) {
  if (props.selectedAutomationRun) {
    return (
      <ForgeAutomationRunRoute
        runId={props.selectedAutomationRun.id}
        organizationId={String(props.selectedAutomationRun.organizationId)}
        onBack={() => props.onSelectAutomationRun(null)}
        onChanged={props.onRefreshAutomations}
      />
    );
  }
  return (
    <ProjectAutomationsPanel
      cmmdLinked={props.home.forgeTarget !== null}
      loading={props.automations.automationLoading}
      creatingPresetKey={props.automations.creatingAutomationPresetKey}
      updatingRuleId={props.automations.updatingAutomationRuleId}
      presets={props.automations.automationPresets}
      rules={props.automations.automationRules}
      runs={props.automations.automationRuns}
      error={props.automations.automationError}
      onCreatePreset={props.automations.handleCreateAutomationPreset}
      onToggleRule={props.automations.handleToggleAutomationRule}
      onSelectRun={(entry) =>
        props.onSelectAutomationRun({
          id: entry.run.id,
          organizationId: entry.run.organizationId,
        } satisfies ForgeAutomationRunTarget)
      }
      onRefresh={props.onRefreshAutomations}
      onLinkCmmdProject={props.onOpenAutomationExecution}
      emptyTitle="Choose a cloud execution to enable automations"
      emptyDescription="Automations run from this project in a cloud execution project. Use an existing one or spin up a new one."
      emptyActionLabel="Choose or create execution"
      hideChromeRefresh
    />
  );
}
